import moment from 'moment'

//
// Chart observations ("Verbraucher"-Notizen an Chart-Punkten).
//
// Das Chart in HomePage.vue ist eine Chartkick-Kategorie-Achse mit
// 'H:mm'-Labels ohne Datum. Damit ein Klick auf einen Punkt wieder zu einem
// Zeitstempel und einem Vorpunkt fuehrt, pflegt firstGlanceData() parallel
// zur Serie eine Meta-Map ueber diesen Builder. Synthetische Punkte
// (Send-on-Change-Backfill, Nullzaehler) werden markiert: sie sind keine
// echten Pakete und duerfen weder Beobachtungen tragen noch als Vorpunkt
// fuer das Delta dienen.
//

export const KIND_ON = 1
export const KIND_OFF = 2
export const KIND_OTHER = 3

const LABEL_FORMAT = 'H:mm'

export function labelFor (iso) {
  if (iso == null) return null
  return moment(iso).format(LABEL_FORMAT)
}

export function seriesKey (systempart, valuetype) {
  return String(systempart) + '/' + String(valuetype)
}

//
// Builder for the per-series point map. Real points win over synthetic
// ones on the same label (the real packet is written AFTER the backfill in
// firstGlanceData) and keep their position in the `real` list — the
// previous real point is real[realIndex - 1].
//
export function createSeriesPoints () {
  const byLabel = {}
  const real = []
  return {
    addReal (label, iso, value, packageId) {
      const entry = { label, iso, value, synthetic: false, packageId: packageId != null ? packageId : null, realIndex: real.length }
      byLabel[label] = entry
      real.push(entry)
      return entry
    },
    addSynthetic (label, iso, value) {
      if (byLabel[label] && !byLabel[label].synthetic) return byLabel[label]
      const entry = { label, iso, value, synthetic: true, packageId: null, realIndex: null }
      byLabel[label] = entry
      return entry
    },
    // n minutes before `stamp` carried prevValue (vt:36 backfill) — same
    // labels sendOnChange.backfillNoChange writes
    addSyntheticRange (stamp, n, prevValue) {
      const count = Math.max(0, Math.floor(n || 0))
      for (let i = 1; i <= count; i++) {
        const at = moment(stamp).subtract(i, 'minutes')
        this.addSynthetic(at.format(LABEL_FORMAT), at.toISOString(), prevValue)
      }
      return count
    },
    build () {
      return { byLabel, real }
    }
  }
}

export function resolvePoint (points, label) {
  if (!points || !points.byLabel || label == null) return null
  return points.byLabel[label] || null
}

export function previousRealPoint (points, entry) {
  if (!points || !entry || entry.synthetic || entry.realIndex == null || entry.realIndex <= 0) return null
  return points.real[entry.realIndex - 1] || null
}

export function defaultKind (delta) {
  if (delta == null || !isFinite(delta) || delta === 0) return KIND_OTHER
  return delta > 0 ? KIND_ON : KIND_OFF
}

//
// Proposal for a clicked label: null when the label is unknown or
// synthetic. delta is null when there is no real previous point (the modal
// then asks the server via /observations/propose).
//
export function proposeObservation (points, label, meta) {
  const entry = resolvePoint(points, label)
  if (!entry || entry.synthetic) return null
  const previous = previousRealPoint(points, entry)
  const delta = previous && previous.value != null && entry.value != null ? entry.value - previous.value : null
  return {
    systempart: meta ? meta.systempart : null,
    valuetype: meta ? meta.valuetype : null,
    observedat: entry.iso,
    value: entry.value,
    previousValue: previous ? previous.value : null,
    previousAt: previous ? previous.iso : null,
    delta,
    kind: defaultKind(delta),
    label
  }
}

//
// First clicked element that belongs to a real series (frmMeta present,
// no prediction). Chart.js passes the elements nearest to the click.
//
export function pickClickedElement (elements, datasets) {
  if (!Array.isArray(elements) || !Array.isArray(datasets)) return null
  for (const element of elements) {
    if (!element) continue
    const dataset = datasets[element.datasetIndex]
    const meta = dataset ? dataset.frmMeta : null
    if (meta && !meta.isPrediction) {
      return { element, meta, dataset }
    }
  }
  return null
}

// { seriesKey: { 'H:mm': observation } } — last writer wins (DST fold)
export function indexByLabel (observations) {
  const index = {}
  for (const o of observations || []) {
    if (!o || o.observedat == null) continue
    const key = seriesKey(o.systempart, o.valuetype)
    if (!index[key]) index[key] = {}
    index[key][labelFor(o.observedat)] = o
  }
  return index
}

export function inWindow (observation, start, end) {
  if (!observation || observation.observedat == null) return false
  const t = moment(observation.observedat).valueOf()
  const from = start != null ? moment(start).valueOf() : -Infinity
  const until = end != null ? moment(end).valueOf() : Infinity
  return t >= from && t <= until
}

export function sortByTime (observations) {
  return (observations || []).slice().sort((a, b) => moment(a.observedat).valueOf() - moment(b.observedat).valueOf())
}

export function formatDelta (delta, unit) {
  if (delta == null || !isFinite(delta)) return ''
  const rounded = Math.round(delta * 1000) / 1000
  const sign = rounded > 0 ? '+' : ''
  const number = Number.isInteger(rounded) ? String(rounded) : rounded.toLocaleString('de-DE', { maximumFractionDigits: 3 })
  return sign + number + (unit ? ' ' + unit : '')
}

export function kindColor (kind) {
  switch (Number(kind)) {
    case KIND_ON: return '#28a745'
    case KIND_OFF: return '#6c757d'
    default: return '#17a2b8'
  }
}

export function kindBadgeClass (kind) {
  switch (Number(kind)) {
    case KIND_ON: return 'bg-success'
    case KIND_OFF: return 'bg-secondary'
    default: return 'bg-info'
  }
}
